import { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { isEqual, isFunction, isUndefined } from 'lodash';
import {
  getPreference,
  isQuotaError,
  migrateLegacyLocalStorageKey,
  setPreference,
  subscribePreferenceChanges,
} from 'services/storage/preferencesIndexedDB';
import { Logger } from '../../utilities';

// Manages UI preference state persisted in IndexedDB with legacy localStorage migration.
export const useLocalStorage = <T = any>(key: string, initialValue?: T) => {
  const [storedValue, setStoredValue] = useState<T>(initialValue as T);
  // IndexedDB reads are async, so callers need an explicit hydration signal
  // before showing persisted UI state such as feature flags or onboarding prompts.
  const [isReady, setIsReady] = useState(false);
  const storedValueRef = useRef(storedValue);
  storedValueRef.current = storedValue;
  // Track the latest initialValue so the effect can reset to it without being
  // listed as a dependency (avoids re-running load on every render).
  const initialValueRef = useRef(initialValue);
  initialValueRef.current = initialValue;

  useEffect(() => {
    let cancelled = false;
    setIsReady(false);

    const load = async () => {
      try {
        await migrateLegacyLocalStorageKey(key);
        const value = await getPreference<T>(key);
        if (!cancelled) {
          const nextValue = value !== undefined ? value : (initialValueRef.current as T);
          // Explicitly reset to initialValue when the key has no stored entry so
          // a key change (e.g. valuations_column_visibility_${sectionId}) does
          // not inherit the previous key's state.
          // Hydration can deliver the same value under a new reference and would otherwise re-render the tree.
          if (!isEqual(storedValueRef.current, nextValue)) {
            storedValueRef.current = nextValue;
            setStoredValue(nextValue);
          }
          setIsReady(true);
        }
      } catch (error) {
        Logger.debug(`Error reading preference key "${key}":`, error);
        if (!cancelled) {
          setIsReady(true);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [key]);

  useEffect(() => subscribePreferenceChanges(({ key: changedKey, value }) => {
    if (changedKey === key) {
      // Same-tab broadcast is delivered twice, so ignore duplicates that only differ by reference.
      if (isEqual(storedValueRef.current, value)) {
        return;
      }
      // Update the ref synchronously so getCurrentValue() is not stale during the
      // window between the BroadcastChannel message and the React re-render commit.
      storedValueRef.current = value as T;
      setStoredValue(value as T);
      // A broadcasted value is already authoritative for this key, even if the
      // initial IndexedDB read is still resolving.
      setIsReady(true);
    }
  }), [key]);

  const setValue = useCallback((value: T | ((prevValue: T) => T)) => {
    setStoredValue(prevStoredValue => {
      const valueToStore = isFunction(value) ? value(prevStoredValue as T) : value;

      if (!isUndefined(valueToStore)) {
        storedValueRef.current = valueToStore;
        setPreference(key, valueToStore).catch(error => {
          if (isQuotaError(error)) {
            Logger.warn(`Quota exceeded saving preference key "${key}"`);
          } else {
            Logger.warn(`Error saving preference key "${key}":`, error);
          }
          Sentry.captureException(error, {
            extra: { preferenceKey: key },
          });
        });

        setIsReady(true);
        return valueToStore;
      }

      return prevStoredValue;
    });
  }, [key]);

  const getCurrentValue = useCallback((): T => storedValueRef.current as T, []);

  return [storedValue, setValue, getCurrentValue, isReady] as const;
};

export default useLocalStorage;
